/** Copyright (c) 2026 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

/** Same copy as the worker app (`err_blank_input`). */
export const BLANK_DATE_INPUT_ERROR = 'Blank inputs are not allowed!';

/** Avoid rendering thousands of year buttons when a range is unbounded. */
export const MAX_YEAR_BUTTONS = 400;

export function dateSubmitError(value: string): string | null {
  if (value.trim() === '') {
    return BLANK_DATE_INPUT_ERROR;
  }
  return null;
}

/** Value shown in the field. A saved answer wins over pre-fill. An empty string means the worker cleared the field, so pre-fill is not restored. */
export function initialDateValue(outputDate: string | null | undefined, preFill: string | null | undefined): string {
  if (typeof outputDate === 'string' && outputDate.length > 0) {
    return outputDate;
  }
  if (typeof preFill === 'string') {
    return preFill;
  }
  return '';
}

/** Pre-fill to persist when the step opens and nothing has been saved yet. Blank pre-fill is skipped so Submit can still report an empty answer. */
export function initialOutputToWrite(
  outputDate: string | null | undefined,
  preFill: string | null | undefined,
): string | null {
  if (typeof outputDate === 'string' && outputDate.length > 0) {
    return null;
  }
  if (typeof preFill === 'string' && preFill.trim() !== '') {
    return preFill;
  }
  return null;
}

export type YearBounds = {
  start: number;
  end: number;
};

/** Year window for the picker. Missing bounds match the worker app: start defaults to 0 and end defaults to the current year. Returns null when a provided bound is not an integer or start is after end. */
export function yearBounds(
  startRange: number | null | undefined,
  endRange: number | null | undefined,
  currentYear: number,
): YearBounds | null {
  const start = resolveYearBound(startRange);
  const end = resolveYearBound(endRange);
  if (start === 'invalid' || end === 'invalid') {
    return null;
  }

  const resolvedStart = start ?? 0;
  const resolvedEnd = end ?? currentYear;
  if (resolvedStart > resolvedEnd) {
    return null;
  }
  return { start: resolvedStart, end: resolvedEnd };
}

/** Inclusive year list for the picker grid. Returns null when the span is too large to render as buttons. */
export function listYears(start: number, end: number): number[] | null {
  const count = end - start + 1;
  if (count > MAX_YEAR_BUTTONS) {
    return null;
  }
  return Array.from({ length: count }, (_, index) => start + index);
}

/** Label for bounds the spec actually set. Defaults used only by the picker are omitted. */
export function yearRangeLabel(
  startRange: number | null | undefined,
  endRange: number | null | undefined,
): string | null {
  const start = integerBound(startRange);
  const end = integerBound(endRange);
  if (start != null && end != null) {
    return `Years ${start}–${end}`;
  }
  if (start != null) {
    return `From ${start}`;
  }
  if (end != null) {
    return `Up to ${end}`;
  }
  return null;
}

function integerBound(value: number | null | undefined): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value;
  }
  return null;
}

export function isYearSelected(value: string, year: number): boolean {
  const trimmed = value.trim();
  const yearText = String(year);
  return trimmed === yearText || trimmed.startsWith(`${yearText}-`) || trimmed.startsWith(`${yearText}/`);
}

function resolveYearBound(value: number | null | undefined): number | null | 'invalid' {
  if (value == null) {
    return null;
  }
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    return 'invalid';
  }
  return value;
}
